import { Box, Link as MuiLink, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CopyableId from '@/components/dataDisplay/CopyableId'
import CustomTypography from '@/components/dataDisplay/CustomTypography'
import PortTypeChip from '@/features/resource/components/PortTypeChip'
import { targetControllerLabel } from '@/features/resource/utils/controllerLabel'
import { portCopyIdLabel } from '@/features/resource/utils/portCopyLabel'
import type { GroupedPort, PortReference } from '@/features/resource/utils/portGroups'
import { truncateId } from '@/utils/truncateId'

/**
 * One field of the response, named the way the API names it.
 *
 * Deliberately not translated: these are identifiers in a payload, and an operator reading this
 * panel is usually holding the response beside it. Only the values and the group headings are prose.
 */
function Field({
  name,
  isApiName = true,
  children,
}: Readonly<{ name: string; isApiName?: boolean; children: React.ReactNode }>) {
  return (
    <>
      <Box component="dt" gridColumn={1}>
        <CustomTypography color="grey.500" fontFamily={isApiName ? 'monospace' : undefined} variant="caption">
          {name}
        </CustomTypography>
      </Box>
      <Box component="dd" gridColumn={2} margin={0} sx={{ overflowWrap: 'anywhere' }}>
        {children}
      </Box>
    </>
  )
}

/**
 * How the panel writes "there is no value here" rather than leaving a blank line.
 *
 * Its own component because three different absences share it — the key never arrived, the key
 * arrived null, and the list arrived empty — and a reader has to be able to tell all three from a
 * value, which a blank line cannot do.
 */
function FieldMarker({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <CustomTypography color="grey.500" fontFamily="monospace" fontStyle="italic" variant="caption">
      {children}
    </CustomTypography>
  )
}

/**
 * A value, with absent told apart from null.
 *
 * The two mean different things here and the types say so: absent is a field msa-resource-bff does
 * not send yet, null is one it sends empty. `ControllerIdentifiers` carries the account of which is
 * which — deliberately not repeated here, since this panel's concern is only that the distinction
 * survives to the screen. Collapsing both to "-" would hide which of the two you are looking at,
 * which is the question this panel exists to answer.
 */
function FieldValue({ value }: Readonly<{ value: string | number | boolean | null | undefined }>) {
  const { t } = useTranslation()

  if (value === undefined || value === null) {
    return (
      <FieldMarker>
        {value === undefined
          ? t('resources.detail_panel.ports_graph_field_absent')
          : t('resources.detail_panel.ports_graph_field_null')}
      </FieldMarker>
    )
  }

  return <CustomTypography variant="body2">{String(value)}</CustomTypography>
}

/**
 * A list field, which has one more way of being empty than a scalar does.
 *
 * An empty array joined into a string is the empty string, and that drew a blank line nothing
 * could tell from a rendering fault. It is a state the response can legitimately send, so it is
 * marked as one.
 */
function FieldIdList({ ids }: Readonly<{ ids: string[] | null | undefined }>) {
  const { t } = useTranslation()

  if (ids == null) {
    return <FieldValue value={ids} />
  }

  if (ids.length === 0) {
    return <FieldMarker>{t('resources.detail_panel.ports_graph_field_empty_list')}</FieldMarker>
  }

  return <CustomTypography variant="body2">{ids.join(', ')}</CustomTypography>
}

function FieldGroupHeading({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Box component="div" gridColumn="1 / -1" paddingTop={1}>
      <CustomTypography color="grey.500" variant="overline">
        {children}
      </CustomTypography>
    </Box>
  )
}

/**
 * A related port, worded as the table words it.
 *
 * The visible label truncates a nameless port's id, but the accessible name keeps it whole: an
 * eight-character fragment ending in an ellipsis is not something a screen reader user can recover,
 * and there is no tooltip here to recover it from.
 */
function RelationLink({
  reference,
  onSelectPort,
}: Readonly<{ reference: PortReference; onSelectPort: (portId: string) => void }>) {
  const { t } = useTranslation()

  /*
   * Same reasoning as the table's cell — reported, but with no control, because there is no row here
   * to select either. Worded without the verb, unlike the table's: this row already carries
   * "Measured by" as its field label, and the table's cell has no label to lean on.
   */
  if (!reference.targetIsListed) {
    return (
      <CustomTypography color="grey.500" variant="body2">
        {t('resources.detail_panel.ports_graph_unlisted_target', { portId: truncateId(reference.targetPortId) })}
      </CustomTypography>
    )
  }

  const label = reference.targetPortName ?? truncateId(reference.targetPortId)
  const spokenName = reference.targetPortName ?? reference.targetPortId
  const accessibleName = reference.isCrossController
    ? t('resources.detail_panel.ports_graph_select_related_on_other_controller', {
        portName: spokenName,
        controllerName: targetControllerLabel(reference, t),
      })
    : t('resources.detail_panel.ports_graph_select_related', { portName: spokenName })

  return (
    <MuiLink
      aria-label={accessibleName}
      component="button"
      sx={{ display: 'block', textAlign: 'left' }}
      type="button"
      onClick={() => onSelectPort(reference.targetPortId)}
    >
      <CustomTypography component="span" variant="inherit">
        {label}
      </CustomTypography>
      {reference.isCrossController && (
        <CustomTypography color="grey.500" component="span" display="block" variant="caption">
          {targetControllerLabel(reference, t)}
        </CustomTypography>
      )}
    </MuiLink>
  )
}

export type PortFieldPanelProps = {
  port: GroupedPort | null
  /**
   * Focused after a jump taken from inside this panel, so the reader lands on the name of the port
   * they moved to. Activating a node keeps focus on the node instead; only a jump from here moves it.
   */
  headingRef?: React.Ref<HTMLElement>
  /** Escape inside the panel clears the selection, as it does on a node. */
  onDismiss?: () => void
  /** False for a LOCAL resource, whose error values stopped being updated. */
  hasLiveDeviceErrors: boolean
  references: readonly PortReference[]
  onSelectPort: (portId: string) => void
}

/**
 * Every field the response carries for the selected port — a node cannot hold a UUID, host a select
 * or render multi-line error text, so what the table does better is reachable from here.
 *
 * The numeric `controllerId` is not listed deliberately: it is the monolith's legacy id, it keys
 * nothing here, and the equipment number is the only controller identifier with a route.
 *
 * `errors` is omitted rather than listed, and only for a LOCAL resource. A frozen value is arguably
 * still honest in a panel reporting what the API returned, but one rule across the table column,
 * the graph's dot and this is worth more than a per-surface exception that has to be explained to
 * every reader who meets it.
 */
export default function PortFieldPanel({
  port,
  references,
  onSelectPort,
  hasLiveDeviceErrors,
  headingRef,
  onDismiss,
}: Readonly<PortFieldPanelProps>) {
  const { t } = useTranslation()

  if (port === null) {
    return (
      <CustomTypography color="grey.500" variant="body2">
        {t('resources.detail_panel.ports_graph_panel_empty')}
      </CustomTypography>
    )
  }

  return (
    /*
     * Escape works anywhere in the panel, not only on a node. A reader who jumped here has focus
     * inside this subtree, so handling it only on the nodes left the one dismissal gesture dead
     * exactly where it had just sent them.
     */
    <Stack
      gap={1}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && onDismiss !== undefined) {
          event.preventDefault()
          onDismiss()
        }
      }}
    >
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
        {/*
         * Focusable only programmatically. A jump taken from inside the panel unmounts the button
         * that was pressed — the port you arrive at does not reference itself — so focus fell to the
         * body. It lands here instead, on the name of the port just moved to.
         */}
        <CustomTypography
          ref={headingRef}
          fontWeight={600}
          // The panel is 300px, and an unbreakable name overflows it by 146px without this.
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          tabIndex={-1}
          variant="subtitle2"
        >
          {port.name ?? t('resources.detail_panel.ports_graph_unnamed_port')}
        </CustomTypography>
        <PortTypeChip port={port} />
      </Stack>

      <Box
        columnGap={2}
        component="dl"
        display="grid"
        gridTemplateColumns="minmax(0, auto) minmax(0, 1fr)"
        margin={0}
        rowGap={0.5}
      >
        {port.kind === 'CONTROL' ? (
          <>
            <Field name="controlPortID">
              <CopyableId copyLabel={portCopyIdLabel(port, t)} value={port.id} />
            </Field>
            <Field name="ports">
              <FieldValue value={port.controlPort.ports} />
            </Field>
            <Field name="category">
              <FieldValue value={port.controlPort.category} />
            </Field>
            <Field name="isOutput">
              <FieldValue value={port.controlPort.isOutput} />
            </Field>
            <Field name="controlBoxType">
              <FieldValue value={port.controlPort.controlBoxType} />
            </Field>
            <Field name="measure">
              <FieldValue value={port.controlPort.measure} />
            </Field>
            <Field name="userDefinedUsableEnergy">
              <FieldValue value={port.controlPort.userDefinedUsableEnergy} />
            </Field>
            {hasLiveDeviceErrors && (
              <Field name="errors">
                <FieldValue value={port.controlPort.errors} />
              </Field>
            )}

            <FieldGroupHeading>{t('resources.detail_panel.ports_graph_field_group_relation')}</FieldGroupHeading>
            {/*
             * Both fields, under the names the response gives them, because this panel's job is to
             * mirror the response rather than to interpret it. `powerMeterID` is deprecated for
             * reading a relation — it is only the first meter — and is shown here precisely because
             * the response still sends it; the relation itself is the list below and the rows under
             * "Measured by".
             */}
            <Field name="powerMeterID">
              <FieldValue value={port.controlPort.powerMeterID} />
            </Field>
            <Field name="powerMeterIDs">
              <FieldIdList ids={port.controlPort.powerMeterIDs} />
            </Field>
          </>
        ) : (
          <>
            <Field name="powerMeterID">
              <CopyableId copyLabel={portCopyIdLabel(port, t)} value={port.id} />
            </Field>
            <Field name="name">
              <FieldValue value={port.meteringPort.name} />
            </Field>
            <Field name="arithmeticOperation">
              <FieldValue value={port.meteringPort.arithmeticOperation} />
            </Field>
            <Field name="isSingleResource">
              <FieldValue value={port.meteringPort.isSingleResource} />
            </Field>
            {hasLiveDeviceErrors && (
              <Field name="errors">
                <FieldValue value={port.meteringPort.errors} />
              </Field>
            )}

            <FieldGroupHeading>{t('resources.detail_panel.ports_graph_field_group_relation')}</FieldGroupHeading>
          </>
        )}

        {references.length === 0 ? (
          <Box gridColumn="1 / -1">
            <CustomTypography color="grey.500" variant="body2">
              {port.kind === 'CONTROL'
                ? t('resources.detail_panel.ports_graph_measured_by_nothing')
                : t('resources.detail_panel.ports_graph_measures_nothing')}
            </CustomTypography>
          </Box>
        ) : (
          <Field
            isApiName={false}
            name={
              port.kind === 'CONTROL'
                ? t('resources.detail_panel.ports_graph_measured_by_caption')
                : t('resources.detail_panel.ports_graph_measures_caption')
            }
          >
            <Stack gap={0.25}>
              {references.map((reference) => (
                <RelationLink key={reference.targetPortId} reference={reference} onSelectPort={onSelectPort} />
              ))}
            </Stack>
          </Field>
        )}

        <FieldGroupHeading>{t('resources.detail_panel.ports_graph_field_group_controller')}</FieldGroupHeading>
        {/* Only metering ports carry a controller name — a control port has none anywhere in the
            pipeline, so the field is not offered rather than shown perpetually empty. */}
        {port.kind === 'METERING' && (
          <Field name="controllerName">
            <FieldValue value={port.meteringPort.controllerName} />
          </Field>
        )}
        <Field name="controllerEquipmentNumber">
          <FieldValue
            value={
              port.kind === 'CONTROL'
                ? port.controlPort.controllerEquipmentNumber
                : port.meteringPort.controllerEquipmentNumber
            }
          />
        </Field>
        <Field name="controllerImei">
          <FieldValue
            value={port.kind === 'CONTROL' ? port.controlPort.controllerImei : port.meteringPort.controllerImei}
          />
        </Field>
      </Box>
    </Stack>
  )
}
